import { useFullscreenPortal } from './chunk-MBTLYAMU.js';
import { cn } from './chunk-FFX3LZW7.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';
import { X, ChevronLeft } from 'lucide-react';

function Skeleton({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("animate-pulse rounded-md bg-muted", className), ...props });
}
var Sheet = SheetPrimitive.Root;
var SheetTrigger = SheetPrimitive.Trigger;
var SheetClose = SheetPrimitive.Close;
function SheetPortal({ container, ...props }) {
  const fullscreen = useFullscreenPortal();
  return /* @__PURE__ */ jsx(SheetPrimitive.Portal, { container: container === void 0 ? fullscreen : container, ...props });
}
var SheetOverlay = React.forwardRef(function SheetOverlay2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    SheetPrimitive.Overlay,
    {
      ref,
      className: cn(
        "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      ),
      ...props
    }
  );
});
var sheetVariants = cva(
  "fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b",
        bottom: "inset-x-0 bottom-0 border-t",
        left: "inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
        right: "inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm"
      }
    },
    defaultVariants: { side: "right" }
  }
);
var SheetContent = React.forwardRef(function SheetContent2({ side = "right", className, children, ...props }, ref) {
  return /* @__PURE__ */ jsxs(SheetPortal, { children: [
    /* @__PURE__ */ jsx(SheetOverlay, {}),
    /* @__PURE__ */ jsxs(SheetPrimitive.Content, { ref, className: cn(sheetVariants({ side }), className), ...props, children: [
      children,
      /* @__PURE__ */ jsxs(SheetPrimitive.Close, { className: "absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-ring", children: [
        /* @__PURE__ */ jsx(X, { className: "size-4" }),
        /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Close" })
      ] })
    ] })
  ] });
});
function SheetHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-col gap-y-2 text-center sm:text-left", className), ...props });
}
function SheetFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className), ...props });
}
var SheetTitle = React.forwardRef(function SheetTitle2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(SheetPrimitive.Title, { ref, className: cn("text-lg font-semibold text-foreground", className), ...props });
});
var SheetDescription = React.forwardRef(function SheetDescription2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(SheetPrimitive.Description, { ref, className: cn("text-sm text-muted-foreground", className), ...props });
});
var WIDTH_CLASS = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-xl",
  "2xl": "sm:max-w-2xl"
};
function DrilldownPanel({
  open,
  onOpenChange,
  title,
  eyebrow,
  subtitle,
  headerRight,
  onBack,
  footer,
  side = "right",
  width = "lg",
  className,
  children
}) {
  return /* @__PURE__ */ jsx(Sheet, { open, onOpenChange, children: /* @__PURE__ */ jsxs(
    SheetContent,
    {
      side,
      className: cn("flex w-full flex-col gap-0 p-0", WIDTH_CLASS[width], className),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3 border-b p-4 pr-12", children: [
          onBack && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: onBack,
              "aria-label": "Back",
              className: "mt-0.5 inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
              children: /* @__PURE__ */ jsx(ChevronLeft, { className: "size-4" })
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
            eyebrow && /* @__PURE__ */ jsx("div", { className: "text-2xs font-medium uppercase tracking-wide text-muted-foreground", children: eyebrow }),
            /* @__PURE__ */ jsx(SheetTitle, { className: "truncate text-base", children: title }),
            subtitle && /* @__PURE__ */ jsx(SheetDescription, { className: "truncate text-xs", children: subtitle })
          ] }),
          headerRight && /* @__PURE__ */ jsx("div", { className: "shrink-0", children: headerRight })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "min-h-0 flex-1 overflow-y-auto p-4", children }),
        footer && /* @__PURE__ */ jsx("div", { className: "flex items-center justify-end gap-2 border-t bg-background p-4", children: footer })
      ]
    }
  ) });
}

export { DrilldownPanel, Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetPortal, SheetTitle, SheetTrigger, Skeleton };
//# sourceMappingURL=chunk-7BRTZT5T.js.map
//# sourceMappingURL=chunk-7BRTZT5T.js.map